import { useState } from "react";
import { DashboardLayout } from "@/components/Dashboard/DashboardLayout";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/apiClient";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import {
  Plus,
  Search,
  Briefcase,
  Wrench,
  Clock,
  CheckCircle2,
  AlertCircle,
  User,
  Calendar,
  Filter,
} from "lucide-react";
import { CreateTaskDialog } from "@/components/Tasks/CreateTaskDialog";
import { TaskCard } from "@/components/Tasks/TaskCard";
import { TaskDetailsDialog } from "@/components/Tasks/TaskDetailsDialog";

const Tasks = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<any>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<"all" | "administrative" | "operative">("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState("");

  // Fetch tasks
  const { data: tasks, isLoading } = useQuery({
    queryKey: ["internal-tasks", activeTab, statusFilter],
    queryFn: async () => {
      let query = api
        .from("internal_tasks")
        .select(`
          *,
          assigned_profile:profiles!assigned_to(id, full_name),
          creator:users!created_by(id, full_name),
          customer:customers!customer_id(id, company_name, codificacion)
        `)
        .order("created_at", { ascending: false });

      if (activeTab !== "all") {
        query = query.eq("task_type", activeTab);
      }

      if (statusFilter !== "all") {
        query = query.eq("status", statusFilter);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data;
    },
  });

  // Filter by search
  const filteredTasks = tasks?.filter((task: any) => {
    if (!searchQuery) return true;
    const search = searchQuery.toLowerCase();
    return (
      task.title?.toLowerCase().includes(search) ||
      task.task_number?.toLowerCase().includes(search) ||
      task.description?.toLowerCase().includes(search)
    );
  });

  // Stats
  const stats = {
    total: tasks?.length || 0,
    pending: tasks?.filter((t: any) => t.status === "pending").length || 0,
    inProgress: tasks?.filter((t: any) => t.status === "in_progress").length || 0,
    completed: tasks?.filter((t: any) => t.status === "completed").length || 0,
  };

  const getStatusBadge = (status: string) => {
    const config: Record<string, { color: string; label: string }> = {
      pending: { color: "bg-slate-500/20 text-slate-700", label: "Pendiente" },
      in_progress: { color: "bg-blue-500/20 text-blue-700", label: "En Progreso" },
      completed: { color: "bg-green-500/20 text-green-700", label: "Completada" },
      cancelled: { color: "bg-red-500/20 text-red-700", label: "Cancelada" },
    };
    const { color, label } = config[status] || config.pending;
    return <Badge className={color}>{label}</Badge>;
  };

  const getPriorityBadge = (priority: string) => {
    const config: Record<string, { color: string; label: string }> = {
      low: { color: "bg-slate-500/20 text-slate-600", label: "Baja" },
      normal: { color: "bg-blue-500/20 text-blue-600", label: "Normal" },
      high: { color: "bg-orange-500/20 text-orange-600", label: "Alta" },
      urgent: { color: "bg-red-500/20 text-red-600", label: "Urgente" },
    };
    const { color, label } = config[priority] || config.normal;
    return <Badge className={color}>{label}</Badge>;
  };

  const handleStatusChange = async (taskId: string, newStatus: string) => {
    try {
      const task = tasks?.find((t: any) => t.id === taskId);
      const updateData: any = { status: newStatus };

      if (newStatus === "in_progress" && !task?.started_at) {
        updateData.started_at = new Date().toISOString();
      }

      if (newStatus === "completed") {
        updateData.completed_at = new Date().toISOString();
        if (task?.started_at) {
          const startTime = new Date(task.started_at).getTime();
          const endTime = Date.now();
          updateData.actual_duration_minutes = Math.round((endTime - startTime) / 60000);
        }
      }

      const { error } = await api.from("internal_tasks").update(updateData).eq("id", taskId);
      if (error) throw error;

      toast.success(
        newStatus === "completed" ? "Tarea completada" :
        newStatus === "in_progress" ? "Tarea iniciada" :
        newStatus === "pending" ? "Tarea reabierta" : "Estado actualizado"
      );
      queryClient.invalidateQueries({ queryKey: ["internal-tasks"] });
    } catch (error: any) {
      toast.error(error.message || "Error al actualizar");
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold">Tareas Internas</h2>
            <p className="text-muted-foreground mt-1">
              Gestión de tareas administrativas y operativas
            </p>
          </div>
          <Button
            onClick={() => setCreateDialogOpen(true)}
            className="bg-gradient-magma hover:shadow-magma-hover"
          >
            <Plus className="w-4 h-4 mr-2" />
            Crear Tarea
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total</p>
                  <p className="text-2xl font-bold">{stats.total}</p>
                </div>
                <Briefcase className="w-8 h-8 text-primary/20" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Pendientes</p>
                  <p className="text-2xl font-bold text-slate-600">{stats.pending}</p>
                </div>
                <Clock className="w-8 h-8 text-slate-500/20" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">En Progreso</p>
                  <p className="text-2xl font-bold text-blue-600">{stats.inProgress}</p>
                </div>
                <AlertCircle className="w-8 h-8 text-blue-500/20" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Completadas</p>
                  <p className="text-2xl font-bold text-green-600">{stats.completed}</p>
                </div>
                <CheckCircle2 className="w-8 h-8 text-green-500/20" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por título, número o descripción..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[180px]">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Estado" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los estados</SelectItem>
              <SelectItem value="pending">Pendiente</SelectItem>
              <SelectItem value="in_progress">En Progreso</SelectItem>
              <SelectItem value="completed">Completada</SelectItem>
              <SelectItem value="cancelled">Cancelada</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as any)}>
          <TabsList>
            <TabsTrigger value="all" className="gap-2">
              <Briefcase className="w-4 h-4" />
              Todas
            </TabsTrigger>
            <TabsTrigger value="administrative" className="gap-2">
              <Briefcase className="w-4 h-4" />
              Administrativas
            </TabsTrigger>
            <TabsTrigger value="operative" className="gap-2">
              <Wrench className="w-4 h-4" />
              Operativas
            </TabsTrigger>
          </TabsList>

          <TabsContent value={activeTab} className="mt-6">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : filteredTasks?.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Briefcase className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
                  <p className="text-muted-foreground">No hay tareas que mostrar</p>
                  <Button
                    variant="outline"
                    className="mt-4"
                    onClick={() => setCreateDialogOpen(true)}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Crear primera tarea
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {filteredTasks?.map((task: any) => (
                  <TaskCard
                    key={task.id}
                    task={task}
                    getStatusBadge={getStatusBadge}
                    getPriorityBadge={getPriorityBadge}
                    onUpdate={() => queryClient.invalidateQueries({ queryKey: ["internal-tasks"] })}
                    onViewDetails={(t) => { setSelectedTask(t); setDetailsOpen(true); }}
                    onEdit={(t) => { setSelectedTask(t); setDetailsOpen(true); }}
                  />
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      <CreateTaskDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ["internal-tasks"] });
          setCreateDialogOpen(false);
        }}
      />

      <TaskDetailsDialog
        task={selectedTask}
        open={detailsOpen}
        onOpenChange={setDetailsOpen}
        onStatusChange={handleStatusChange}
      />
    </DashboardLayout>
  );
};

export default Tasks;
